// @author jonathan.ronsten@svenskaspel.se

import type { trackEvent } from '@svs/nova-core/nextjs/analytics';

import type { Product } from '../../@types/index.js';
import type { ClientErrorCode } from '../../error/constants.js';
import type { ClientErrorDetails } from '../../error/getClientErrorDetails.js';
import type {
  InteractionErrorProps,
  InteractionPostPaymentMiddlewareFn,
  InteractionSuccessProps,
  OnInteraction,
} from '../../flow/types.js';
import type { PostMiddlewareFn } from '../../payment/createPayment.js';
import type { Wager } from '../../wager/Wager.js';
import { getErrorMessage } from './messages/getErrorMessage.js';
import { getPaymentMessage } from './messages/getPaymentMessage.js';

type TrackingAction = 'my-games' | 'ok';

const trackEventAction = ({
  trackingAction,
  paidProducts,
  trackEventFunction,
}: {
  trackingAction: TrackingAction;
  paidProducts: Product[];
  trackEventFunction?: typeof trackEvent;
}): void => {
  if (trackEventFunction) {
    trackEventFunction({
      category: 'overlays',
      action: trackingAction,
      optLabel: paidProducts[0],
    });
  }
};

type PaymentDoneSuccessInteraction = InteractionSuccessProps & {
  message?: string | undefined;
  mybetsUrl: string;
  homeUrl: string;
  isRetailer?: boolean;
  isExternalWager?: boolean;
  onCallback: (action: TrackingAction) => void;
};

type PaymentDoneErrorInteraction = InteractionErrorProps & {
  clientCode?: ClientErrorCode;
  clientErrors: ClientErrorDetails;
};

/**
 * Middleware that triggers a payment done dialog.
 *
 * @param onInteraction The callback to trigger the interaction.
 * @param options props
 * @param options.paymentUrls Urls for redirecting after payment
 * @param options.currencyFormatter Optional currency formatter function to format prices in the dialog messages
 * @param options.trackEventFunction Optional function to track analytics events, used for tracking when users interact with the payment done dialog.
 * @returns The middleware function.
 */
export function paymentDone(
  onInteraction: OnInteraction<PaymentDoneSuccessInteraction, PaymentDoneErrorInteraction>,
  options?: {
    paymentUrls?: Record<string, string | undefined>;
    currencyFormatter?: (amount: number | string, options?: { shouldUseDecimals?: boolean; unit?: string }) => string;
    trackEventFunction?: typeof trackEvent;
  }
): PostMiddlewareFn<Wager> {
  const fn: ReturnType<InteractionPostPaymentMiddlewareFn<Wager>> =
    ({ paymentInfo, paymentResponse }) =>
    async (next) => {
      try {
        const { paymentUrls, currencyFormatter, trackEventFunction } = options ?? {};
        const { result, error } = paymentResponse;
        const paidItems = result.getPaidPayableItems();

        if (paidItems.length > 0) {
          const mybetsUrl = paymentUrls?.['mybetsHome'] ?? '/mina-spel';
          const homeUrl = paymentUrls?.['home'] ?? '/';
          const marketplaceWager = paidItems.find((wager) => wager.getIsMarketplaceWager());
          const [dialogTitle, dialogMessage] = getPaymentMessage({
            paidItems,
            unpaidItems: result.getUnpaidPayableItems(),
            paymentDTO: result.getPaidPayableItemsDTO(),
            paymentInfo,
            currencyFormatter,
          });

          const isExternalWager = paidItems.some((wager) => wager.hasOwnRows);

          const paidProducts = paidItems.map((wager) => wager.productBet?.product).filter(Boolean) as Product[];

          const interactionProps = {
            title: dialogTitle,
            message: dialogMessage,
            mybetsUrl,
            homeUrl,
            isRetailer: marketplaceWager && marketplaceWager.customer === 'Retailer',
            isExternalWager,
            onCallback: (action: TrackingAction): void => {
              trackEventAction({ trackingAction: action, paidProducts, trackEventFunction });
            },
          };

          await onInteraction(interactionProps);
        } else if (error) {
          const clientErrors = await getErrorMessage(error, paymentInfo, result.getUnpaidPayableItems());
          const clientCode = undefined;
          void onInteraction(undefined, {
            clientErrors,
            clientCode,
          });
        }
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        const err = error instanceof Error ? error : new Error(`${error}`);

        void onInteraction(undefined, {
          clientErrors: await getErrorMessage(err, paymentInfo),
        });
      }

      next();
    };

  return fn;
}
